import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import {
  apdmDraftStatusLabels,
  apdmReviewIntentLabels,
  rpaReviewMessageLabels,
} from '@euler/functions/apdm/reviewDraft';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { OrgMarketingFollowupFeedPayload } from '@euler/model/apdm';
import { useState } from 'react';
import { View } from 'react-native';
import { CustomerMessageWell } from './CustomerMessageWell';
import { FollowupActions } from './FollowupActions';
import { FollowupStatus } from './FollowupStatus';
import { NoticeLine } from './NoticeLine';
import { OfferSection } from './OfferSection';
import { PlateTitle } from './PlateTitle';
import { ApdmDraftReview } from './draft-review/ApdmDraftReview';
import { DraftWell } from './draft-review/DraftWell';

const kNoTexts: readonly string[] = [];
const kRead = { kind: 'read' } as const;

/**
 * `OrgMarketingFollowup` — a campaign's customer needs the staff member: the
 * car and campaign, what is asked and why, the customer's words, the offer
 * they are on, and what to do — review the reply drafted for them, or
 * answer in WeChat. The thread is manual-read: 标记已读 clears it.
 */
export function MarketingFollowupBody({
  p,
  messageId,
  readAt,
  onMarkRead,
}: {
  p: OrgMarketingFollowupFeedPayload;
  messageId?: string;
  readAt?: string | null;
  onMarkRead?: (id: string) => void | Promise<void>;
}) {
  const { rpx } = useResponsiveContext();
  const navigation = useAppNavigation();
  const [reviewing, setReviewing] = useState(false);
  const [readFailed, setReadFailed] = useState(false);
  const shopOffer = p.offer.currentShopOffer;
  const markRead =
    messageId && !readAt && onMarkRead
      ? () => {
          void (async () => {
            try {
              await onMarkRead(messageId);
              setReadFailed(false);
            } catch {
              setReadFailed(true);
            }
          })();
        }
      : null;
  return (
    <View>
      {/* `p.title` is not drawn: it is always 营销跟进, the timeline's
          category above the body. */}
      <PlateTitle plate={p.licensePlateNo} campaign={p.campaignTitle} />
      <FollowupStatus
        message={p.message}
        why={reviewing ? null : apdmReviewIntentLabels[p.why]}
      />
      {reviewing ? null : (
        <>
          {p.customerMessages.length ? (
            <View style={{ marginTop: rpx(12), gap: rpx(8) }}>
              {p.customerMessages.map(message => (
                <CustomerMessageWell
                  key={message.id}
                  sender="客户"
                  sentAt={message.sendTime}
                  text={message.text || rpaReviewMessageLabels[message.type]}
                />
              ))}
            </View>
          ) : null}
          <OfferSection
            coupon={p.offer.currentCoupon}
            texts={shopOffer?.kind === 'text' ? shopOffer.texts : kNoTexts}
          />
        </>
      )}
      {p.event.kind === 'draft' ? (
        reviewing ? (
          <ApdmDraftReview
            storeId={p.refs.storeId}
            draftId={p.event.ref.draftId}
            onDone={async () => {
              if (messageId) await onMarkRead?.(messageId);
            }}
          />
        ) : (
          <>
            <DraftWell
              status={apdmDraftStatusLabels[p.event.status]}
              text={p.event.text}
              state={kRead}
            />
            <FollowupActions
              primary={{
                title: '审核最新草稿',
                onPress: () => setReviewing(true),
              }}
              onMarkRead={markRead}
            />
          </>
        )
      ) : (
        <>
          <NoticeLine text="请在客户微信会话中人工回复。" />
          <FollowupActions
            primary={{
              title: '查看活动客户 →',
              onPress: () =>
                navigation.navigate('ApdmCampaign', {
                  campaignId: p.refs.campaignId,
                }),
            }}
            onMarkRead={markRead}
          />
        </>
      )}
      {reviewing && markRead ? (
        <FollowupActions primary={null} onMarkRead={markRead} />
      ) : null}
      {readFailed ? (
        <NoticeLine tone="danger" text="未能更新已读状态，请刷新消息后重试。" />
      ) : null}
    </View>
  );
}
